import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/minimal';
import { Severity } from '@sentry/types';
import {
  Route as BasicRoute,
  // eslint-disable-next-line no-restricted-imports
  RouteProps as BasicRouteProps,
  matchPath,
  useLocation,
} from 'react-router-dom';
import snakeCase from 'lodash/snakeCase';
import mapKeys from 'lodash/mapKeys';
import queryString from 'query-string';
import { AnalyticsPageContextData } from '@modernhealth/analytics-core';

import {
  AnalyticsPageviewProperties,
  AnalyticsProperties,
} from './AnalyticsClient';
import { AnalyticsPageContext, useAnalyticsClient } from './ReactModule';

export interface RouteProps extends BasicRouteProps {
  path?: string; // omit string[] from BasicRouteProps interface
  analytics: {
    pageName: string;
  } & AnalyticsProperties;
}

type LocationState = Record<string, unknown> | undefined;

interface Cache {
  sent: Record<string, boolean>;
  ref: Record<string, number>;
  maxDepth: Record<string, number>;
}

const localCache: Cache = {
  sent: {},
  ref: {},
  maxDepth: {},
};

/**
 * Queue a page view event to allow all matching routes to register
 * an event for a particular path match before we send a single
 * tracking beacon out for the best match.
 *
 * - Why do we need this?
 *
 *   react-router supports nesting routes and route declarations
 *   anywhere within the code – within routes, within routes
 *   within routes, etc... Because of this there is not a 1:1
 *   between route matches and Route component declarations. A
 *   parent route does not know if it contains any children routes
 *   that could be matching the url more precisely than itself.
 *
 *   As an example, `/course/:slug` will match on
 *   the subroute `/course/:slug/sections/:sectionSlug` in addition to
 *   the subroute matching (two matches); however, it is less precise
 *   and will be missing the `:sectionSlug` param from its matching
 *   information. We want to capture all of this and use the page name
 *   from the more precise route match which more accurately represents
 *   the page the user is seeing.
 *
 * - How is this solving the problem?
 *
 *   The queueEvent function solves this problem with three main
 *   directives: (1) allowing all routes that find a match to register
 *   a page view for a path name, (2) only triggering a single event
 *   to be sent to our analytics warehouse based on which is the best
 *   match, and (3) preventing double page views by preventing any
 *   route that may register after a page view has already been sent
 *   to the warehouse from being sent.
 *
 *   The latter could occur if there is a nested subroute which is
 *   conditionally rendered and there is a delay of its rendering
 *   (and matching) – lets say due to a slow network request of the
 *   parent route requesting a resource before rendering some children.
 *   I do not know if this exists in our code but it is a valid logic
 *   flow that react-router supports so it is plausible for it to
 *   occur.
 *
 * - Are there alternative approaches?
 *
 *   Because of the dynamic and free form approach react-router takes
 *   to Route declaration there are not a lot of options unless
 *   want to fight the route setting paradigm it employs.
 *
 *   (1) One alternative to our dynamic approach would be to centrally
 *   define all route configuration in a manifest. If we did this we
 *   would know of every route that exists in the app so we could
 *   feasibly determine if any child routes are going to match a
 *   particular path. This would require its own logic flow and isn't
 *   necessarily trivial to determine. This becomes potentially error
 *   prone in the long run because it solely relies on this manifest
 *   being both up to date and accurate to send page view events.
 */
export function queueEvent(
  path: { pathName: string; pathPattern: string; exact: boolean },
  send: () => void,
  cache = localCache,
): {
  dispatch(): void;
  flush(): void;
} {
  const { pathName, pathPattern, exact } = path;
  const { sent, ref, maxDepth } = cache;
  const pathDepth = pathPattern.split('/').length;

  if (sent[pathName] === true) {
    return {
      dispatch: () => null,
      flush: () => null,
    };
  }

  ref[pathName] = (ref[pathName] || 0) + 1;

  if (exact) {
    sent[pathName] = true;
    send();
  } else {
    maxDepth[pathName] = Math.max(maxDepth[pathName] || 0, pathDepth);
  }

  function dispatch() {
    if (!sent[pathName] && maxDepth[pathName] === pathDepth) {
      send();
      sent[pathName] = true;
    }
  }

  let flushed = false;
  function flush() {
    if (flushed) {
      return;
    }

    dispatch();

    ref[pathName] = ref[pathName] - 1;
    if (ref[pathName] <= 0) {
      delete ref[pathName];
      delete sent[pathName];
      delete maxDepth[pathName];
    }

    flushed = true;
  }

  return {
    dispatch,
    flush,
  };
}

export const Route: React.FC<RouteProps> = ({ analytics, ...props }) => {
  // Use the useLocation hook instead of the location prop because
  // the location prop is only passed into a Route if the parent
  // component is a Router component such as Switch. Openly defined
  // Route declarations do not have any route props injected in by
  // a parent and thus those props cannot be relied upon here.
  const location = useLocation<LocationState>();
  const analyticsClient = useAnalyticsClient();
  const [pageContext, setPageContext] = useState<
    AnalyticsPageContextData<AnalyticsPageviewProperties>
  >({});

  useEffect(() => {
    const computedMatch = matchPath(location.pathname, {
      path: props.path,
      exact: props.exact,
    });

    if (!computedMatch || !props.path) {
      Sentry.withScope(scope => {
        scope.setExtra('location.pathname', location.pathname);
        scope.setExtra('path', props.path);
        scope.setExtra('match', computedMatch);
        scope.setLevel(Severity.Warning);
        Sentry.captureMessage(
          'No match or path was found when attempting to track pageview',
        );
      });
      return;
    }

    const pathName = location.pathname;
    const exact = props.exact || computedMatch.url === pathName;

    /**
     * About searchParams: in PostAuth.tsx (member-webapp-frontend repo) we clear URL queryParams (by not providing them to redirectFn after authentication)
     * and because of that we can't get it here from location.search (it will be present here only for non-authenticated routes) so we pass
     * those params to state.searchParams (in member-webapp-frontend), parse it here and send to track event. Thanks this we can track query params (for example UTM params)
     * without tracking all the location.state object which could contain internal, not-public data.
     */
    const searchParams = {
      ...(typeof location.state?.searchParams === 'string'
        ? queryString.parse(location.state.searchParams)
        : {}),
      ...queryString.parse(location.search),
    };

    const event: AnalyticsPageviewProperties = {
      ...mapKeys(analytics, (_, k) => snakeCase(k)),
      ...mapKeys(computedMatch.params, (_, k) => snakeCase(k)),
      ...mapKeys(searchParams, (_, k) => snakeCase(k)),
      search: location.search,
      page_name: analytics.pageName,
      path_name: pathName,
      path_pattern: props.path,
      path_pattern_exact: exact,
      event_source: 'autotrack',
    };

    setPageContext(event);

    const queue = queueEvent(
      { pathName: event.path_name, pathPattern: event.path_pattern, exact },
      () => analyticsClient.trackPageView(event),
    );

    // Send out the page view after a short period. This should
    // reasonably allow subroutes to register without waiting too
    // long potentially throwing off the timestamp on the event
    // and the order events are received in.
    //
    // IMPORTANT NOTE: exact matches are fired off instantly.
    // This is only used to send a page view if no exact route
    // match is found within the timeout window.
    const timeoutId = setTimeout(queue.dispatch, 200);

    return () => {
      clearTimeout(timeoutId);
      queue.flush();
    };
    // We purposefully do not want to fire new page views unless the
    // exact deps specified have changed. It is understood that some
    // data we are using is not included as a change in it should NOT
    // fire a new page view. It would end up firing a duplicate page
    // view event which would inflate usage numbers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    analyticsClient,
    analytics.pageName,
    location.pathname,
    location.search,
    location.state?.searchParams,
    props.path,
    props.exact,
  ]);

  return (
    <AnalyticsPageContext.Provider value={pageContext}>
      <BasicRoute {...props} />
    </AnalyticsPageContext.Provider>
  );
};
